import React, { useEffect, useState } from "react";
import { CompaniaManager } from "managers";
import "./DesvincularCuentaQuickbooks.styles.scss";

/**
 * Página pública del callback de desconexión de Intuit.
 *
 * Cuando un usuario hace "Disconnect" desde QuickBooks Online
 * (Apps → My Apps → Citrus eCF → Disconnect), Intuit redirige al usuario
 * a esta URL con `?realmId=<id>` en el query string. Esta página invoca
 * el endpoint público `DesvincularCuentaQuickbooksPorRealmId` para limpiar
 * la integración del lado de Citrus eCF.
 *
 * No requiere sesión; se monta directamente desde `App.js` antes del
 * chequeo de autenticación.
 */

// SVG icons — outline minimal, currentColor for theming
const Icon = {
    Pause: () => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="6" y="4" width="4" height="16" />
            <rect x="14" y="4" width="4" height="16" />
        </svg>
    ),
    Lock: () => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
    ),
    Document: () => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="8" y1="13" x2="16" y2="13" />
            <line x1="8" y1="17" x2="13" y2="17" />
        </svg>
    ),
    Chart: () => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="20" x2="12" y2="10" />
            <line x1="18" y1="20" x2="18" y2="4" />
            <line x1="6" y1="20" x2="6" y2="16" />
        </svg>
    ),
    Arrow: () => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
        </svg>
    ),
};

const DesvincularCuentaQuickbooks = () => {
    const quickbooksLogo = require("../../assets/img/quickbooks-brand-preferred-logo.png");
    const citrusLogo = require("../../assets/img/Citrus-Logo.png");

    // Estados: "loading" | "success" | "error" | "invalid"
    const [state, setState] = useState("loading");
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const realmId = params.get("realmId");

        if (!realmId) {
            setState("invalid");
            return;
        }

        CompaniaManager.desvincularQuickbooksPorRealmId(realmId)
            .then(() => setState("success"))
            .catch((e) => {
                setErrorMessage(
                    e && e.message
                        ? e.message
                        : "No pudimos completar la desvinculación. Intenta nuevamente más tarde."
                );
                setState("error");
            });
    }, []);

    // ── Bloques reusables ────────────────────────────────────

    const Logos = () => (
        <div className="qb-logos">
            <img src={citrusLogo} alt="Citrus eCF" className="qb-logo" />
            <span className="qb-logo-sep" aria-hidden="true" />
            <img src={quickbooksLogo} alt="QuickBooks Online" className="qb-logo" />
        </div>
    );

    const ReconnectSection = () => (
        <div className="qb-section">
            <h2 className="qb-section-title">Cómo reconectar tu cuenta</h2>
            <p className="qb-reconnect-intro">
                Puedes volver a vincular QuickBooks Online a Citrus eCF en
                cualquier momento siguiendo estos pasos:
            </p>
            <ol className="qb-steps">
                <li>
                    <span className="qb-step-num" />
                    <span className="qb-step-text">
                        <a href="/">Inicia sesión en Citrus eCF</a> con tu usuario y
                        contraseña habituales.
                    </span>
                </li>
                <li>
                    <span className="qb-step-num" />
                    <span className="qb-step-text">
                        Dirígete a <strong>Configurar Compañía</strong> desde el menú
                        principal.
                    </span>
                </li>
                <li>
                    <span className="qb-step-num" />
                    <span className="qb-step-text">
                        En la sección <strong>Sistemas Externos</strong>, selecciona{" "}
                        <strong>QuickBooks</strong> como tipo de integración.
                    </span>
                </li>
                <li>
                    <span className="qb-step-num" />
                    <span className="qb-step-text">
                        Haz clic en <strong>Connect to QuickBooks</strong> y autoriza
                        nuevamente a Citrus eCF en la ventana de Intuit.
                    </span>
                </li>
                <li>
                    <span className="qb-step-num" />
                    <span className="qb-step-text">
                        La integración quedará activa y el procesamiento automático
                        de e-CFs se reanudará.
                    </span>
                </li>
            </ol>
        </div>
    );

    const CTAs = () => (
        <div className="qb-cta-row">
            <a href="/" className="qb-cta-primary">
                Iniciar sesión
                <Icon.Arrow />
            </a>
            <a href="/quickbooks-integration" className="qb-cta-secondary">
                Sobre la integración
            </a>
        </div>
    );

    const Footer = () => (
        <div className="qb-card-footer">
            ¿Necesitas ayuda? Escríbenos a{" "}
            <a href="mailto:soporte@citrus.com.do">soporte@citrus.com.do</a>
        </div>
    );

    // ── Loading ──────────────────────────────────────────────
    if (state === "loading") {
        return (
            <div className="qb-disconnect-page">
                <div className="qb-loading">
                    <div className="qb-spinner" />
                    <h4>Procesando desvinculación</h4>
                    <p>Estamos limpiando tu integración con QuickBooks Online.</p>
                </div>
            </div>
        );
    }

    // ── Invalid ──────────────────────────────────────────────
    if (state === "invalid") {
        return (
            <div className="qb-disconnect-page">
                <div className="qb-disconnect-card">
                    <div className="qb-header qb-header-warning">
                        <Logos />
                        <div className="qb-badge qb-badge-warning">
                            <span className="qb-badge-dot" />
                            Enlace incompleto
                        </div>
                        <h1 className="qb-title">Esta página requiere un realmId</h1>
                        <p className="qb-lead">
                            Probablemente llegaste aquí por accidente. Este enlace solo
                            funciona cuando es invocado por QuickBooks Online al
                            desconectar la integración.
                        </p>
                    </div>
                    <div className="qb-body">
                        <p className="qb-reconnect-intro" style={{ marginBottom: 24 }}>
                            Si lo que querías era <strong>gestionar tu integración</strong>{" "}
                            con QuickBooks Online, puedes hacerlo directamente desde tu
                            cuenta de Citrus eCF.
                        </p>
                        <ReconnectSection />
                        <CTAs />
                    </div>
                    <Footer />
                </div>
            </div>
        );
    }

    // ── Error ────────────────────────────────────────────────
    if (state === "error") {
        return (
            <div className="qb-disconnect-page">
                <div className="qb-disconnect-card">
                    <div className="qb-header qb-header-danger">
                        <Logos />
                        <div className="qb-badge qb-badge-danger">
                            <span className="qb-badge-dot" />
                            No completado
                        </div>
                        <h1 className="qb-title">No pudimos desvincular tu cuenta</h1>
                        <p className="qb-lead">{errorMessage}</p>
                    </div>
                    <div className="qb-body">
                        <div className="qb-notice">
                            <strong>Esto puede ocurrir si</strong> la cuenta ya fue
                            desvinculada anteriormente, o si el realmId no coincide con
                            ninguna cuenta vinculada actualmente en Citrus eCF.
                        </div>
                        <ReconnectSection />
                        <CTAs />
                    </div>
                    <Footer />
                </div>
            </div>
        );
    }

    // ── Success ──────────────────────────────────────────────
    return (
        <div className="qb-disconnect-page">
            <div className="qb-disconnect-card">
                <div className="qb-header qb-header-accent">
                    <Logos />
                    <div className="qb-badge qb-badge-success">
                        <span className="qb-badge-dot" />
                        Desvinculado
                    </div>
                    <h1 className="qb-title">¡Hasta pronto, QuickBooks!</h1>
                    <p className="qb-lead">
                        Tu cuenta de QuickBooks Online ha sido desvinculada de Citrus eCF
                        de forma segura.
                    </p>
                </div>

                <div className="qb-body">
                    {/* Qué significa esto */}
                    <div className="qb-section">
                        <h2 className="qb-section-title">¿Qué significa esto?</h2>
                        <ul className="qb-info-list">
                            <li className="qb-info-item">
                                <div className="qb-info-icon"><Icon.Pause /></div>
                                <div className="qb-info-text">
                                    <strong>Procesamiento detenido</strong>
                                    <span>
                                        Ya no leeremos ni procesaremos nuevos documentos
                                        desde tu cuenta de QuickBooks Online.
                                    </span>
                                </div>
                            </li>
                            <li className="qb-info-item">
                                <div className="qb-info-icon"><Icon.Lock /></div>
                                <div className="qb-info-text">
                                    <strong>Tokens revocados</strong>
                                    <span>
                                        Los tokens OAuth fueron revocados en Intuit y
                                        eliminados de nuestros servidores.
                                    </span>
                                </div>
                            </li>
                            <li className="qb-info-item">
                                <div className="qb-info-icon"><Icon.Document /></div>
                                <div className="qb-info-text">
                                    <strong>Tus e-CFs permanecen</strong>
                                    <span>
                                        Los NCFs, PDFs y XMLs firmados siguen intactos
                                        en tus registros de QuickBooks.
                                    </span>
                                </div>
                            </li>
                            <li className="qb-info-item">
                                <div className="qb-info-icon"><Icon.Chart /></div>
                                <div className="qb-info-text">
                                    <strong>Tu historial sigue disponible</strong>
                                    <span>
                                        Toda tu información y reportes en Citrus eCF se
                                        mantienen accesibles desde tu panel.
                                    </span>
                                </div>
                            </li>
                        </ul>
                    </div>

                    {/* Cómo reconectar */}
                    <ReconnectSection />

                    {/* CTAs */}
                    <CTAs />
                </div>

                <Footer />
            </div>
        </div>
    );
};

export default DesvincularCuentaQuickbooks;
